import React from 'react';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WorkIcon from '@material-ui/icons/Work';
import { Storage } from 'aws-amplify';
import {
    ContainerFieldsList,
    CustomBreadcrumbs,
    CustomSelectInput,
} from 'candy-commons-components';
import {
    ArrayInput,
    DeleteButton,
    Edit,
    ImageField,
    SaveButton,
    SimpleForm,
    SimpleFormIterator,
    SimpleShowLayout,
    TextInput,
    Toolbar,
    required,
    useTranslate,
} from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import ImageInput from '../../components/ImageInput';
import loadingTypes from '../../data/loadingType.json';
import installationTypes from '../../data/installationType.json';
import dryingTechnologies from '../../data/dryingTechnology.json';
import tankPositions from '../../data/tankPosition.json';
import { useSelector } from 'react-redux';
import {
    getApplianceTypes,
    getBrands,
    getSeries,
    getMaintenanceInstructionsCategories,
    getDrawerTypes,
    getSeriesVersion,
    x,
} from '../../selectors/cmsConfig';
const MaintenanceEditSaveButton = ({
    handleSubmitWithRedirect,
    isEditDisabled,
    ...props
}) => {
    const form = useForm();
    const formState = useFormState();
    const handleClick = () => {
        if (formState.values.generic) form.change('link', null);
        handleSubmitWithRedirect('list');
    };
    return (
        <SaveButton
            {...props}
            handleSubmitWithRedirect={handleClick}
            disabled={isEditDisabled}
        />
    );
};
const MaintenanceEditToolbar = ({
    permissions = {},
    resource,
    isEditDisabled,
    ...props
}) => (
    <Toolbar {...props}>
        <MaintenanceEditSaveButton isEditDisabled={isEditDisabled} />
        <span style={{ flex: '1' }} />
        {permissions[resource] &&
            permissions[resource].delete &&
            !isEditDisabled && <DeleteButton undoable />}
    </Toolbar>
);
const MaintenanceEdit = ({ permissions, ...props }) => {
    const classes = useStyles();
    const { id } = props;
    const translation = useTranslate();
    const applianceTypes = useSelector(getApplianceTypes);
    const brands = useSelector(getBrands);
    const series = useSelector(getSeries);
    const seriesVersions = useSelector(getSeriesVersion);
    const drawerTypes = useSelector(getDrawerTypes);
    const categories = useSelector(getMaintenanceInstructionsCategories);
    const isEditDisabled = !permissions?.maintenance?.update;
    const pageTitle = `Id: ${id}`;
    const breadcrumbsData = [
        {
            text: translation('resources.maintenance-instructions.name'),
            url: 'maintenance-instructions',
            icon: <WorkIcon />,
        },
        {
            text: pageTitle,
            url: '',
        },
    ];
    return (
        <>
            <CustomBreadcrumbs data={breadcrumbsData} />
            <Edit className={classes.reverseButtonSave} {...props}>
                <SimpleForm
                    toolbar={
                        <MaintenanceEditToolbar
                            permissions={permissions}
                            isEditDisabled={isEditDisabled}
                        />
                    }
                >
                    <Grid container style={{ width: '100%', paddingLeft: 10 }}>
                        <Typography>Criteria</Typography>
                    </Grid>
                    <ContainerFieldsList>
                        <CustomSelectInput
                            allowEmpty
                            source="applianceType"
                            validate={required()}
                            choices={applianceTypes}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            source="brand"
                            allowEmpty
                            choices={brands}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            source="series"
                            allowEmpty
                            choices={series}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            source="seriesVersion"
                            allowEmpty
                            choices={seriesVersions}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            label="Loading Type"
                            source="loadingType"
                            allowEmpty
                            choices={loadingTypes}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            label="Drawer Type"
                            source="drawerType"
                            allowEmpty
                            choices={drawerTypes}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            label="Installation Type"
                            source="installationType"
                            allowEmpty
                            choices={installationTypes}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            label="Drying Technology"
                            source="dryingTechnology"
                            allowEmpty
                            choices={dryingTechnologies}
                            disabled={isEditDisabled}
                        />
                        <CustomSelectInput
                            label="Tank Position"
                            source="tankPosition"
                            allowEmpty
                            choices={tankPositions}
                            disabled={isEditDisabled}
                        />
                        <TextInput
                            label="Unit"
                            source="unit"
                            allowEmpty
                            disabled
                        />
                    </ContainerFieldsList>
                    <Divider style={{ width: '100%' }} />
                    <Grid container style={{ width: '100%', paddingLeft: 10 }}>
                        <Typography>Maintenance Categoy</Typography>
                    </Grid>
                    <ContainerFieldsList>
                        <CustomSelectInput
                            source="category"
                            allowEmpty
                            validate={required()}
                            choices={categories}
                            disabled={isEditDisabled}
                        />
                    </ContainerFieldsList>
                    <Divider style={{ width: '100%' }} />
                    <ArrayInput source="steps" disabled={isEditDisabled}>
                        <SimpleFormIterator
                            getItemLabel={(index) => `${index + 1}-key`}
                        >
                            <SimpleShowLayout>
                                <ImageField
                                    source="imagePath.src"
                                    label="Image"
                                />
                            </SimpleShowLayout>
                            <TextInput
                                label="Title"
                                source="title"
                                validate={required()}
                            />
                            <TextInput
                                label="Description"
                                source="description"
                            />
                            <TextInput label="CTA Label" source="cta_label" />
                            {!isEditDisabled && (
                                <ImageInput
                                    source="imagePath"
                                    label="Image"
                                    accept="image/*"
                                    placeholder={<p>Drop your file here</p>}
                                >
                                    <ImageField source="src" title="title" />
                                </ImageInput>
                            )}
                        </SimpleFormIterator>
                    </ArrayInput>
                    <Divider style={{ width: '100%', marginBottom: 20 }} />
                    <ContainerFieldsList>
                        <Grid
                            container
                            style={{ width: '100%', paddingLeft: 10 }}
                        >
                            <Typography>Confirmation</Typography>
                        </Grid>
                        <TextInput
                            source="confirmationTitle"
                            allowEmpty
                            disabled={isEditDisabled}
                        />
                        <TextInput
                            source="confirmationDesc"
                            allowEmpty
                            disabled={isEditDisabled}
                        />
                        <TextInput
                            source="confirmationLabel"
                            allowEmpty
                            disabled={isEditDisabled}
                        />
                        <TextInput
                            source="cancelLabel"
                            allowEmpty
                            disabled={isEditDisabled}
                        />
                    </ContainerFieldsList>
                </SimpleForm>
            </Edit>
        </>
    );
};
const useStyles = makeStyles({
    reverseButtonSave: {
        '& .MuiToolbar-root': {
            flexDirection: 'row-reverse',
        },
    },
});
export default MaintenanceEdit;
